import React, { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api, { apiError, timeAgo, fmtTime } from "@/lib/api";
import { useWorkspace } from "@/context/WorkspaceContext";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  PhoneCall, MessageCircle, MessageSquare, Search, Send, ShieldCheck, Play,
  Clock, User, ExternalLink, Voicemail, Sparkles,
} from "lucide-react";

const CH_ICON = { call: PhoneCall, whatsapp: MessageCircle, sms: MessageSquare };
const CH_COLOR = { call: "text-blue-400", whatsapp: "text-emerald-400", sms: "text-purple-400" };
const CH_BG = { call: "bg-blue-500/15", whatsapp: "bg-emerald-500/15", sms: "bg-purple-500/15" };

const FILTERS = [
  { id: "all", label: "All" },
  { id: "call", label: "Calls", testid: "filter-channel-voice" },
  { id: "whatsapp", label: "WhatsApp", testid: "filter-channel-whatsapp" },
  { id: "sms", label: "SMS", testid: "filter-channel-sms" },
];

export default function Inbox() {
  const { config } = useWorkspace();
  const navigate = useNavigate();
  const location = useLocation();
  const [convos, setConvos] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [active, setActive] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [channel, setChannel] = useState("whatsapp");
  const [text, setText] = useState("");
  const [template, setTemplate] = useState("");
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/inbox?channel=${filter}`);
    setConvos(data);
    return data;
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const focus = location.state?.focus;
    if (focus) {
      load().then((list) => {
        const c = list.find((x) => x.contact.id === focus);
        if (c) selectContact(c.contact);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.ts]);

  const selectContact = async (contact) => {
    setActive(contact);
    const { data } = await api.get(`/contacts/${contact.id}/timeline`);
    setTimeline(data);
    setChannel(data.whatsapp_window_open ? "whatsapp" : "whatsapp");
    setTemplate("");
    setText("");
  };

  const filtered = convos.filter((c) =>
    !search || c.contact.name.toLowerCase().includes(search.toLowerCase()) || c.contact.phone.includes(search)
  );

  const windowOpen = timeline?.whatsapp_window_open;

  const send = async () => {
    if (!active) return;
    const useTemplate = channel === "whatsapp" && !windowOpen;
    if (channel === "whatsapp" && !windowOpen && !template) {
      toast.error("24h window closed — pick an approved template.");
      return;
    }
    const body = useTemplate && template
      ? (config?.wa_templates.find((t) => t.name === template)?.body || "").replace("{{name}}", active.name)
      : text;
    if (!body) return;
    setSending(true);
    try {
      await api.post("/messages", {
        workspace_id: localStorage.getItem("workspace_id"),
        contact_id: active.id,
        channel,
        body,
        template_name: useTemplate ? template : null,
      });
      setText("");
      setTemplate("");
      await selectContact(active);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="h-[calc(100vh-4rem)] flex">
      {/* Left: conversation stream */}
      <div className="w-72 md:w-80 flex-shrink-0 border-r border-border/60 bg-[#05261D]/40 flex flex-col">
        <div className="p-3 border-b border-border/60">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-muted-foreground" />
            <Input data-testid="search-contacts-input" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search contacts…" className="pl-8 bg-card/50 h-9" />
          </div>
          <div className="flex gap-1 mt-2">
            {FILTERS.map((f) => (
              <button key={f.id} data-testid={f.testid} onClick={() => setFilter(f.id)}
                className={`flex-1 text-[11px] py-1.5 rounded-md transition-colors ${filter === f.id ? "bg-gold/20 text-gold" : "text-muted-foreground hover:bg-card/60"}`}>
                {f.label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex-1 overflow-auto">
          {filtered.length === 0 && (
            <p className="text-center text-xs text-muted-foreground p-8">No conversations yet. Use “Simulate Inbound”.</p>
          )}
          {filtered.map((c) => {
            const Icon = CH_ICON[c.last_message.channel];
            return (
              <button key={c.contact.id} data-testid={`convo-${c.contact.id}`} onClick={() => selectContact(c.contact)}
                className={`w-full text-left p-3 border-b border-border/40 flex gap-3 transition-colors ${active?.id === c.contact.id ? "bg-primary/30" : "hover:bg-card/40"}`}>
                <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${CH_BG[c.last_message.channel]}`}>
                  <Icon className={`w-4 h-4 ${CH_COLOR[c.last_message.channel]}`} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium truncate">{c.contact.name}</span>
                    <span className="text-[10px] text-muted-foreground flex-shrink-0">{timeAgo(c.last_message.created_at)}</span>
                  </div>
                  <p className="text-xs text-muted-foreground truncate mt-0.5">{c.last_message.body}</p>
                  {c.ticket && (
                    <Badge variant="outline" className={`mt-1.5 text-[9px] py-0 h-4 ${c.ticket.status === "open" ? "border-blue-400/50 text-blue-300" : c.ticket.status === "pending" ? "border-amber-400/50 text-amber-300" : "border-emerald-400/50 text-emerald-300"}`}>
                      {c.ticket.status}
                    </Badge>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Center: timeline */}
      <div className="flex-1 flex flex-col min-w-0 bg-[#02140F]">
        {!active ? (
          <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground">
            <Sparkles className="w-10 h-10 text-gold/40 mb-3" />
            <p className="text-sm">Select a conversation to view the unified timeline</p>
          </div>
        ) : (
          <>
            <div className="h-14 flex items-center justify-between px-4 border-b border-border/60 glass">
              <div className="flex items-center gap-2">
                <span className="font-medium">{active.name}</span>
                <span className="text-xs text-muted-foreground font-mono">{active.phone}</span>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  data-testid="inbox-call-btn"
                  onClick={async () => {
                    try {
                      const res = await api.post("/voice/call-customer", { contact_id: active.id });
                      toast.success(`Calling ${active.name}! Customer sees: ${res.data.caller_id_shown}`);
                      selectContact(active);
                    } catch (e) {
                      toast.error(apiError(e.response?.data?.detail));
                    }
                  }}
                  className="gap-1.5 border-gold/40 text-gold hover:bg-gold/15 text-xs h-8"
                >
                  <PhoneCall className="w-3.5 h-3.5" /> Call Customer
                </Button>
                <Button variant="ghost" size="sm" data-testid="open-customer-profile" onClick={() => navigate(`/customers/${active.id}`)} className="gap-1.5 text-gold text-xs h-8">
                  <ExternalLink className="w-3.5 h-3.5" /> Profile
                </Button>
              </div>
            </div>

            <div className="flex-1 overflow-auto p-4 space-y-3">
              {timeline?.messages.map((m) => {
                const outbound = m.direction === "outbound";
                const Icon = CH_ICON[m.channel];
                const isCall = m.channel === "call";
                return (
                  <div key={m.id} className={`flex ${outbound ? "justify-end" : "justify-start"} fade-in`}>
                    <div className={`max-w-[78%] rounded-2xl px-3.5 py-2.5 ${outbound ? "bg-primary/50 rounded-br-sm" : "bg-card/70 rounded-bl-sm"} border border-border/50`}>
                      <div className="flex items-center gap-1.5 mb-1">
                        <Icon className={`w-3 h-3 ${CH_COLOR[m.channel]}`} />
                        <span className="eyebrow text-[9px] text-muted-foreground">{m.channel}{m.meta?.template_name ? ` · ${m.meta.template_name}` : ""}</span>
                        {m.meta?.consent && <ShieldCheck className="w-3 h-3 text-gold" title="Consent captured" />}
                      </div>
                      <p className="text-sm leading-relaxed">{m.body}</p>
                      {isCall && m.meta?.recording && (
                        <div className="mt-2 flex items-center gap-2 bg-black/30 rounded-lg px-2.5 py-1.5" data-testid="call-recording-player-toggle">
                          {m.meta.recording_type === "voicemail" ? <Voicemail className="w-3.5 h-3.5 text-amber-400" /> : <Play className="w-3.5 h-3.5 text-gold" />}
                          <div className="flex-1 h-1 bg-gold/20 rounded-full overflow-hidden">
                            <div className="h-full bg-gold/60 w-1/3" />
                          </div>
                          <span className="text-[10px] font-mono text-muted-foreground">{Math.floor((m.meta.duration || 0) / 60)}:{String((m.meta.duration || 0) % 60).padStart(2, "0")}</span>
                        </div>
                      )}
                      <p className="text-[10px] text-muted-foreground mt-1 text-right">{fmtTime(m.created_at)}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Composer */}
            <div className="border-t border-border/60 p-3 glass">
              <div className="flex items-center gap-2 mb-2">
                <Select value={channel} onValueChange={setChannel}>
                  <SelectTrigger className="w-32 h-8 bg-card/50 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="whatsapp">WhatsApp</SelectItem>
                    <SelectItem value="sms">SMS</SelectItem>
                  </SelectContent>
                </Select>
                {channel === "whatsapp" && (
                  <Badge data-testid="whatsapp-24h-status-badge" variant="outline"
                    className={`text-[10px] gap-1 ${windowOpen ? "border-emerald-400/50 text-emerald-300" : "border-amber-400/50 text-amber-300"}`}>
                    <Clock className="w-3 h-3" />
                    {windowOpen ? "24h window open" : "Window closed · template only"}
                  </Badge>
                )}
              </div>

              {channel === "whatsapp" && !windowOpen ? (
                <div className="flex gap-2">
                  <Select value={template} onValueChange={setTemplate}>
                    <SelectTrigger data-testid="template-select" className="flex-1 bg-card/50"><SelectValue placeholder="Choose approved template…" /></SelectTrigger>
                    <SelectContent>
                      {config?.wa_templates.map((t) => (
                        <SelectItem key={t.name} value={t.name}>{t.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button data-testid="send-whatsapp-template-btn" onClick={send} disabled={sending || !template}
                    className="bg-gold text-[#0a2e22] hover:bg-gold/90"><Send className="w-4 h-4" /></Button>
                </div>
              ) : (
                <div className="flex gap-2 items-end">
                  <Textarea data-testid="composer-input" value={text} onChange={(e) => setText(e.target.value)}
                    placeholder="Type a reply…" rows={1} className="resize-none bg-card/50 min-h-[40px]"
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} />
                  <Button data-testid="send-message-btn" onClick={send} disabled={sending || !text}
                    className="bg-gold text-[#0a2e22] hover:bg-gold/90"><Send className="w-4 h-4" /></Button>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Right: dossier */}
      <div className="w-72 md:w-80 flex-shrink-0 border-l border-border/60 bg-[#05261D]/50 overflow-auto hidden lg:block">
        {active ? (
          <div className="p-4">
            <div className="flex flex-col items-center text-center pb-4 border-b border-border/60">
              <div className="w-16 h-16 rounded-full bg-primary/50 border border-gold/30 flex items-center justify-center text-2xl font-serif text-gold">{active.name[0]}</div>
              <p className="font-medium mt-2">{active.name}</p>
              <p className="text-xs text-muted-foreground font-mono">{active.phone}</p>
              <div className="flex flex-wrap gap-1 justify-center mt-2">
                {(active.tags || []).map((t) => <Badge key={t} variant="outline" className="text-[10px] border-gold/40 text-gold">{t}</Badge>)}
              </div>
            </div>
            <div className="py-4 space-y-3 text-sm">
              {active.email && <InfoRow icon={User} label="Email" value={active.email} />}
              {timeline?.ticket && <InfoRow icon={Clock} label="Open ticket" value={timeline.ticket.subject} />}
            </div>
            <Button data-testid="dossier-view-profile" onClick={() => navigate(`/customers/${active.id}`)} variant="outline" className="w-full border-gold/40 text-gold gap-1.5">
              <ExternalLink className="w-3.5 h-3.5" /> Document Vault & Payments
            </Button>
          </div>
        ) : (
          <div className="p-8 text-center text-xs text-muted-foreground">Customer dossier appears here</div>
        )}
      </div>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="w-3.5 h-3.5 text-gold mt-0.5 flex-shrink-0" />
      <div className="min-w-0">
        <p className="text-[10px] text-muted-foreground uppercase tracking-wide">{label}</p>
        <p className="truncate">{value}</p>
      </div>
    </div>
  );
}
